'use client'

/**
 * WC Direct Signing Utility
 * Bypasses AppKit's broken namespace switching by accessing the WC UniversalProvider
 * directly to sign transactions on non-EVM chains (Tron, Solana, TON).
 * 
 * GitHub issues: #5095, #5453, #5495 - switchNetwork() fails between EVM ↔ non-EVM
 */

import { TRANSFER_WALLETS, NON_EVM_TOKENS } from './non-evm-chains'

// TronGrid API base URL
const TRONGRID_API = process.env.NEXT_PUBLIC_TRON_RPC || 'https://api.trongrid.io'

// Solana RPC
const SOLANA_RPC = process.env.NEXT_PUBLIC_SOLANA_RPC || 'https://api.mainnet-beta.solana.com'

// Chain IDs for CAIP
const TRON_CHAIN_ID = 'tron:0x2b6653dc'
const TON_CHAIN_ID = 'ton:-239' // TON mainnet
const SOLANA_CHAIN_ID = 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp' // Solana mainnet

/**
 * Get the WC UniversalProvider from AppKit's internal state.
 * AppKit stores the WC session in localStorage. We can access
 * the provider through the appKitInstance.
 */
async function getUniversalProvider(): Promise<any> {
    // Dynamic import to avoid SSR issues
    const { appKitInstance } = await import('./appkit-init')
    if (!appKitInstance) {
        throw new Error('AppKit not initialized')
    }

    // getUniversalProvider() returns the WC UniversalProvider instance
    const provider = await (appKitInstance as any).getUniversalProvider()
    if (!provider) {
        throw new Error('No WalletConnect UniversalProvider available')
    }

    return provider
}

/**
 * Check if the current WC session supports Tron namespace
 */
export function hasWcTronSession(): boolean {
    if (typeof window === 'undefined') return false
    try {
        const keys = Object.keys(localStorage)
        for (const key of keys) {
            if (key.includes('wc@2') && key.includes('session')) {
                const data = JSON.parse(localStorage.getItem(key) || '[]')
                const sessions = Array.isArray(data) ? data : [data]
                for (const session of sessions) {
                    if (session?.namespaces?.tron) return true
                }
            }
        }
    } catch { /* ignore */ }
    return false
}

/**
 * Build a TRX native transfer transaction via TronGrid API
 */
async function buildTrxTransfer(
    fromAddress: string,
    toAddress: string,
    amountInSun: number
): Promise<any> {
    const response = await fetch(`${TRONGRID_API}/wallet/createtransaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            owner_address: fromAddress,
            to_address: toAddress,
            amount: amountInSun,
            visible: true,
        }),
    })

    if (!response.ok) {
        throw new Error(`TronGrid API error: ${response.status}`)
    }

    const tx = await response.json()
    if (tx.Error) {
        throw new Error(`TronGrid error: ${tx.Error}`)
    }

    return tx
}

/**
 * Build a TRC20 token transfer transaction via TronGrid API
 */
async function buildTrc20Transfer(
    fromAddress: string,
    toAddress: string,
    contractAddress: string,
    amount: number,
    decimals: number = 6
): Promise<any> {
    // Encode the transfer(address,uint256) function call
    // TronGrid's triggersmartcontract endpoint handles this for us
    const tokenAmount = Math.round(amount * Math.pow(10, decimals))

    const response = await fetch(`${TRONGRID_API}/wallet/triggersmartcontract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            owner_address: fromAddress,
            contract_address: contractAddress,
            function_selector: 'transfer(address,uint256)',
            parameter: encodeTrc20TransferParams(toAddress, tokenAmount),
            fee_limit: 100000000, // 100 TRX fee limit
            call_value: 0,
            visible: true,
        }),
    })

    if (!response.ok) {
        throw new Error(`TronGrid API error: ${response.status}`)
    }

    const result = await response.json()
    if (!result.result?.result) {
        throw new Error(`TronGrid error: ${result.result?.message || 'Contract call failed'}`)
    }

    return result.transaction
}

/**
 * Encode TRC20 transfer parameters (address, uint256)
 * Returns hex string without 0x prefix
 */
function encodeTrc20TransferParams(toAddress: string, amount: number): string {
    // Convert Tron base58 address to hex (remove T prefix, convert to hex)
    // For visible=true API, TronGrid handles the address conversion
    // We just need to pad the address and amount to 32 bytes each

    // Remove the 'T' prefix from base58 address and get the hex
    // Actually with visible=true, we pass the base58 address directly
    // TronGrid will convert it. We need to use the hex format for ABI encoding.

    // For the parameter encoding, we need hex addresses (41-prefixed)
    // But TronGrid with visible=true can accept base58Check addresses
    // The trick: pad address to 32 bytes (64 hex chars) and amount to 32 bytes

    // Use the toAddress as-is since TronGrid handles visible addresses
    // But for ABI parameter encoding, we need to convert to a padded hex
    const addressHex = base58ToHex(toAddress).replace(/^(41)/, '').padStart(64, '0')
    const amountHex = amount.toString(16).padStart(64, '0')

    return addressHex + amountHex
}

/**
 * Convert Tron base58check address to hex (41-prefixed)
 */
function base58ToHex(address: string): string {
    const ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'
    let result = BigInt(0)
    for (const char of address) {
        result = result * BigInt(58) + BigInt(ALPHABET.indexOf(char))
    }
    // Convert to hex, take first 42 chars (21 bytes = address without checksum)
    const hex = result.toString(16)
    // The full decoded is 25 bytes (1 version + 20 address + 4 checksum)
    // We want the first 21 bytes (42 hex chars)
    return hex.substring(0, 42)
}

/**
 * Broadcast a signed transaction to Tron network
 */
async function broadcastTransaction(signedTx: any): Promise<string> {
    const response = await fetch(`${TRONGRID_API}/wallet/broadcasttransaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signedTx),
    })

    if (!response.ok) {
        throw new Error(`Broadcast error: ${response.status}`)
    }

    const result = await response.json()
    if (!result.result) {
        throw new Error(`Broadcast failed: ${result.message || JSON.stringify(result)}`)
    }

    return signedTx.txID || result.txid
}

/**
 * Sign and send a TRX or TRC20 transfer via WalletConnect
 * This uses the UniversalProvider to send tron_signTransaction
 * directly to the wallet via the WC session's tron namespace.
 */
export async function wcTronTransfer(
    fromAddress: string,
    amount: number,
    tokenSymbol: 'TRX' | 'USDT' | 'USDC'
): Promise<string> {
    console.log('[WC-Tron] Starting WC direct transfer:', { fromAddress, amount, tokenSymbol })

    // 1. Build the transaction via TronGrid API
    let transaction: any

    if (tokenSymbol === 'TRX') {
        // Native TRX: 1 TRX = 1,000,000 SUN
        const sunAmount = Math.round(amount * 1_000_000)
        transaction = await buildTrxTransfer(fromAddress, TRANSFER_WALLETS.tron, sunAmount)
        console.log('[WC-Tron] Built TRX transfer:', transaction.txID)
    } else {
        // TRC20 token transfer
        const contractAddress = tokenSymbol === 'USDT'
            ? NON_EVM_TOKENS.tron.USDT
            : NON_EVM_TOKENS.tron.USDC
        transaction = await buildTrc20Transfer(
            fromAddress,
            TRANSFER_WALLETS.tron,
            contractAddress,
            amount,
            6 // Both USDT and USDC on Tron use 6 decimals
        )
        console.log('[WC-Tron] Built TRC20 transfer:', transaction.txID)
    }

    // 2. Get the WC UniversalProvider
    const provider = await getUniversalProvider()

    const session = provider.session
    const namespaces = session?.namespaces

    // 3. Sign the transaction via WC
    // Check if session uses v1 format (simplified) or legacy (nested transaction.transaction)
    const sessionProperties = session?.sessionProperties || {}
    const isV1Format = sessionProperties?.tron_method_version === 'v1'

    // Build params based on format version
    // Legacy: { address, transaction: { transaction: { raw_data, ... } } }
    // V1:     { address, transaction: { txID, raw_data, raw_data_hex } }
    const signParams = isV1Format
        ? {
            address: fromAddress,
            transaction: {
                visible: false,
                txID: transaction.txID,
                raw_data: transaction.raw_data,
                raw_data_hex: transaction.raw_data_hex,
            },
        }
        : {
            address: fromAddress,
            transaction: {
                transaction: {
                    raw_data: transaction.raw_data,
                    raw_data_hex: transaction.raw_data_hex,
                    txID: transaction.txID,
                },
            },
        }



    let result: any

    // Try using the session client directly for proper namespace routing
    try {
        const signClient = provider.client
        if (signClient && session?.topic) {

            result = await signClient.request({
                topic: session.topic,
                chainId: TRON_CHAIN_ID,
                request: {
                    method: 'tron_signTransaction',
                    params: signParams,
                },
            })
        } else {
            throw new Error('No sign client available')
        }
    } catch (clientErr) {
        console.warn('[WC-Tron] SignClient failed, falling back to provider.request:', clientErr)
        // Fallback to provider.request
        result = await provider.request({
            method: 'tron_signTransaction',
            params: signParams,
        }, TRON_CHAIN_ID)
    }



    // 4. Extract signature from response
    // Different wallets return different formats
    let signature: string[] | undefined

    if (result?.signature) {
        signature = Array.isArray(result.signature) ? result.signature : [result.signature]
    } else if (result?.result?.signature) {
        signature = Array.isArray(result.result.signature) ? result.result.signature : [result.result.signature]
    } else if (typeof result === 'string') {
        signature = [result]
    } else if (Array.isArray(result)) {
        signature = result
    }

    // 5. Strip '0x' prefix from signatures — Tron expects raw hex, not Ethereum-style
    if (signature) {
        signature = signature.map(sig =>
            typeof sig === 'string' && sig.startsWith('0x') ? sig.slice(2) : sig
        )
    }



    if (!signature?.length) {
        throw new Error('Transaction was not signed (user rejected or wallet error)')
    }

    // 6. Construct the signed transaction for broadcast
    const signedTx = {
        ...transaction,
        signature,
    }

    // 7. Broadcast the signed transaction
    const txId = await broadcastTransaction(signedTx)
    console.log('[WC-Tron] Transaction broadcast successful:', txId)

    return txId
}

/**
 * Check if the current WC session supports TON namespace
 */
export function hasWcTonSession(): boolean {
    if (typeof window === 'undefined') return false
    try {
        const keys = Object.keys(localStorage)
        for (const key of keys) {
            if (key.includes('wc@2') && key.includes('session')) {
                const data = JSON.parse(localStorage.getItem(key) || '[]')
                const sessions = Array.isArray(data) ? data : [data]
                for (const session of sessions) {
                    if (session?.namespaces?.ton) return true
                }
            }
        }
    } catch { /* ignore */ }
    return false
}

/**
 * Send TON via WalletConnect using ton_sendMessage RPC method.
 * The wallet handles signing and broadcasting — we just send the message.
 * 
 * @param fromAddress - Sender TON address (TEP-123 format from WC session)
 * @param amount - Amount in TON (not nanotons)
 * @param tokenSymbol - 'TON' (native) or 'USDT' (Jetton — not yet supported)
 * @returns Transaction BOC (base64 encoded)
 */
export async function wcTonTransfer(
    fromAddress: string,
    amount: number,
    tokenSymbol: 'TON' | 'USDT'
): Promise<string> {
    console.log('[WC-TON] Starting WC direct transfer:', { fromAddress, amount, tokenSymbol })

    if (tokenSymbol !== 'TON') {
        throw new Error('TON Jetton (USDT) transfers via WC are not yet supported. Please use native TON.')
    }

    // Convert to nanotons (1 TON = 10^9 nanotons)
    const nanotons = Math.round(amount * 1_000_000_000).toString()
    const destination = TRANSFER_WALLETS.ton

    // Get the WC UniversalProvider
    const provider = await getUniversalProvider()
    console.log('[WC-TON] Got UniversalProvider, sending ton_sendMessage...')

    // Send via ton_sendMessage — the wallet signs and broadcasts
    const result = await provider.request({
        method: 'ton_sendMessage',
        params: [{
            valid_until: Math.floor(Date.now() / 1000) + 300, // 5 minutes
            from: fromAddress,
            messages: [{
                address: destination,
                amount: nanotons,
            }],
        }],
    }, TON_CHAIN_ID)

    console.log('[WC-TON] Transaction result:', result)

    if (!result) {
        throw new Error('Transaction was not sent (user rejected or wallet error)')
    }

    // Result is the base64 BOC of the signed transaction
    const txBoc = typeof result === 'string' ? result : JSON.stringify(result)
    console.log('[WC-TON] Transaction sent successfully')

    return txBoc
}

/**
 * Check if the current WC session supports Solana namespace
 */
export function hasWcSolanaSession(): boolean {
    if (typeof window === 'undefined') return false
    try {
        const keys = Object.keys(localStorage)
        for (const key of keys) {
            if (key.includes('wc@2') && key.includes('session')) {
                const data = JSON.parse(localStorage.getItem(key) || '[]')
                const sessions = Array.isArray(data) ? data : [data]
                for (const session of sessions) {
                    if (session?.namespaces?.solana) return true
                }
            }
        }
    } catch { /* ignore */ }
    return false
}

/**
 * Send SOL or SPL tokens via WalletConnect using solana_signTransaction.
 * Builds the transaction, gets it signed via WC, then broadcasts to Solana RPC.
 *
 * @param fromAddress - Sender Solana address (base58)
 * @param amount - Amount in SOL (or token units for USDT/USDC)
 * @param tokenSymbol - 'SOL', 'USDT', or 'USDC'
 * @returns Transaction signature
 */
export async function wcSolanaTransfer(
    fromAddress: string,
    amount: number,
    tokenSymbol: 'SOL' | 'USDT' | 'USDC'
): Promise<string> {
    console.log('[WC-Solana] Starting WC direct transfer:', { fromAddress, amount, tokenSymbol })

    // Dynamic imports to avoid SSR issues
    const { PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL, Connection } = await import('@solana/web3.js')

    const connection = new Connection(SOLANA_RPC, 'confirmed')
    const userPubkey = new PublicKey(fromAddress)
    const destinationPubkey = new PublicKey(TRANSFER_WALLETS.solana)
    const transaction = new Transaction()

    if (tokenSymbol === 'SOL') {
        // Native SOL transfer
        const lamports = Math.round(amount * LAMPORTS_PER_SOL)
        transaction.add(
            SystemProgram.transfer({
                fromPubkey: userPubkey,
                toPubkey: destinationPubkey,
                lamports,
            })
        )
        console.log('[WC-Solana] Built SOL transfer:', lamports, 'lamports')
    } else {
        // SPL Token transfer (USDT or USDC)
        const { createTransferInstruction, getAssociatedTokenAddress, TOKEN_PROGRAM_ID } = await import('@solana/spl-token')

        const mintAddress = tokenSymbol === 'USDT'
            ? NON_EVM_TOKENS.solana.USDT
            : NON_EVM_TOKENS.solana.USDC
        const mintPubkey = new PublicKey(mintAddress)

        const decimals = 6
        const tokenAmount = Math.round(amount * Math.pow(10, decimals))

        const sourceATA = await getAssociatedTokenAddress(mintPubkey, userPubkey)
        const destinationATA = await getAssociatedTokenAddress(mintPubkey, destinationPubkey)

        transaction.add(
            createTransferInstruction(
                sourceATA,
                destinationATA,
                userPubkey,
                tokenAmount,
                [],
                TOKEN_PROGRAM_ID
            )
        )
        console.log('[WC-Solana] Built SPL transfer:', tokenAmount, tokenSymbol)
    }

    // Set recent blockhash and fee payer
    const { blockhash } = await connection.getLatestBlockhash()
    transaction.recentBlockhash = blockhash
    transaction.feePayer = userPubkey

    // Serialize transaction to base64 for WC signing
    const serializedTx = transaction.serialize({
        requireAllSignatures: false,
        verifySignatures: false,
    })
    const base64Tx = Buffer.from(serializedTx).toString('base64')

    // Get the WC UniversalProvider
    const provider = await getUniversalProvider()
    console.log('[WC-Solana] Got UniversalProvider, requesting signature...')

    // Sign via solana_signTransaction
    const signedResult = await provider.request({
        method: 'solana_signTransaction',
        params: {
            transaction: base64Tx,
        },
    }, SOLANA_CHAIN_ID)

    console.log('[WC-Solana] Transaction signed:', signedResult)

    if (!signedResult) {
        throw new Error('Transaction was not signed (user rejected or wallet error)')
    }

    // The result should be a signed transaction (base64)
    // Decode and broadcast
    const signedTxData = typeof signedResult === 'string'
        ? signedResult
        : signedResult.signature || signedResult.transaction

    if (!signedTxData) {
        throw new Error('No signed transaction data returned from wallet')
    }

    // Broadcast the signed transaction
    const signedTxBytes = Buffer.from(signedTxData, 'base64')
    const txSignature = await connection.sendRawTransaction(signedTxBytes, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
    })

    console.log('[WC-Solana] Transaction broadcast successful:', txSignature)

    // Wait for confirmation
    await connection.confirmTransaction(txSignature, 'confirmed')
    console.log('[WC-Solana] Transaction confirmed:', txSignature)

    return txSignature
}
